// Stony Brook University: public college page (#2740), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Stony Brook's Common Data Set 2025-2026 (Office of Institutional
//    Research, Planning and Effectiveness), published as an Excel workbook
//    on its Common Data Set page: C1 applicants, admits and enrollees for
//    Fall 2025 with the residency table, C8 test policy for Fall 2027 and
//    C8F (scores required for two honors programs), C21/C22 (early action,
//    no early decision, no counts). Its dates in C14, C16 and C22 are for
//    the earlier cycle, so dates come from the admissions pages. Its G1 is
//    marked as not available for 2026-2027 ("Check here if your
//    institution's 2026-2027 academic year costs of attendance are not
//    available"), so the cost comes from Undergraduate Admissions' 2026-2027
//    Tuition and Costs page.
//  - stonybrook.edu undergraduate admissions: How to Apply (deadlines) and
//    First-Year (deadlines, decision dates, test policy, restricted majors).
//  - College Scorecard API, school id 196097 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Stony Brook Common Data Set 2025-2026',
        url: 'https://www.stonybrook.edu/irpe/_media/Common_data_sets/CDS_2025-2026.xlsx',
        read: '2026-10-01',
    },
    apply: {
        label: 'Stony Brook Undergraduate Admissions, How to Apply',
        url: 'https://www.stonybrook.edu/undergraduate-admissions/apply/index.html',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Stony Brook Undergraduate Admissions, First-Year',
        url: 'https://www.stonybrook.edu/undergraduate-admissions/first-year/index.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'Stony Brook Undergraduate Admissions, Tuition and Costs 2026-2027',
        url: 'https://www.stonybrook.edu/undergraduate-admissions/cost-and-aid/tuition-and-costs.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?196097-Stony-Brook-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'stony-brook-university',
    kbId: 'stony_brook_university',
    name: 'Stony Brook University',
    shortName: 'Stony Brook',
    aliases: ['Stony Brook', 'SUNY Stony Brook'],
    location: 'Stony Brook, NY',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Stony Brook brand colors page); decoration only.
    branding: {
        primary: '#990000',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/stony_brook_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 64197,
        admitted: 30930,
        enrolled: 3964,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 42719, admitted: 17065 },
            { group: 'Out-of-state', applied: 13031, admitted: 8198 },
            { group: 'International', applied: 8447, admitted: 5667 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Stony Brook\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Stony Brook has no early decision plan. Its early action plan is nonbinding and not restrictive, and the Common Data Set gives no early action counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Admission to Stony Brook does not guarantee a place in these restricted majors, which ask for more:',
        rows: [
            { school: 'College of Engineering and Applied Sciences majors', requirement: 'Applicants need 4 units of math, including calculus, and 4 units of science, including physics, for most majors. Strong applicants typically have outstanding grades in high school calculus and physics.' },
            { school: 'College of Business (Accounting, Business Management)', requirement: 'Successful applicants typically show achievement in mathematics and writing.' },
            { school: 'Creative Writing', requirement: 'Submit the Creative Writing Supplemental (a statement of purpose and a writing sample) through the StartSBU Portal by the application deadline.' },
            { school: 'Music', requirement: 'Admitted students start as Pre-Music and audition for the major; auditions and placement exams are held at the start of each semester.' },
            { school: 'Nursing, Social Work, Respiratory Care, Clinical Laboratory Sciences', requirement: 'Upper-division majors. You enter as a first-year student in another major, take the prerequisites, then apply to the program.' },
        ],
        note: 'Students admitted to the university but not to their first-choice major can apply to it later by meeting the requirements in the Undergraduate Catalog.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'December 11 or January 29', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'February 26 or March 26', binding: false },
        ],
        note: 'These are completion deadlines: your application, recommendation and transcript must arrive by the date. Every early action applicant complete by November 1 has a decision by January 29; some hear on December 11. The priority deadline for the honors programs is November 1 and the final deadline is January 15 (Simons STEM Scholars applications are due November 1).',
        source: 'firstYear',
        binding: 'Early action is nonbinding. Spring 2027 first-year applications are due November 1.',
        bindingSource: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Stony Brook is test optional: SAT and ACT scores are optional, and admission is a holistic review of your academic record and personal factors. Scores are required for the Scholars for Medicine and Scholars for Dental Medicine programs. If you send scores, they must come directly from the testing agency.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants, and notes that scores are required for the Scholars for Medicine and Scholars for Dental Medicine honors programs.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [7070, 31050] },
            { item: 'Fees', amounts: [3860, 3860] },
            { item: 'Housing (on campus)', amounts: [12291, 12291] },
            { item: 'Meal plan', amounts: [7450, 7450] },
            { item: 'Books', amounts: [900, 900] },
            { item: 'Transportation', amounts: [840, 840] },
            { item: 'Personal expenses', amounts: [2408, 2408] },
        ],
        totals: [34819, 58799],
        note: 'Stony Brook\'s estimated yearly cost for a full-time undergraduate living on campus, as of 02/20/2026; tuition is set by SUNY, usually in June, so the charges can change. Housing is the standard double room rate. Full-time students are also billed for health insurance unless they show other coverage, which is not in the totals.',
        source: 'costs',
        netPrice: {
            average: 18902,
            byIncome: [
                { income: '$0 to $30,000', amount: 13938 },
                { income: '$30,001 to $48,000', amount: 14754 },
                { income: '$48,001 to $75,000', amount: 20285 },
                { income: '$75,001 to $110,000', amount: 24140 },
                { income: 'Over $110,000', amount: 28664 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Stony Brook\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
